/**
 * Devoirs — UI création + suivi avec ciblage par CLASSE et/ou ÉLÈVES.
 *
 * Règles produit :
 *  - Aucun professeur indépendant : seul un user qui enseigne dans une
 *    classe (madrassa validée) peut créer un devoir. Sinon, l'onglet
 *    « Créer » + l'onglet « Envoyés » sont masqués.
 *  - Plus de saisie d'email : on choisit une ou plusieurs classes parmi
 *    celles qu'on enseigne, puis on raffine éventuellement la sélection
 *    en cochant des élèves précis dans chaque classe.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Send, Inbox, Plus, Eye, RefreshCcw, CheckCircle2,
  Clock, Sparkles, BookOpen, Users, GraduationCap, AlertCircle,
  ChevronDown, ChevronRight, Settings as SettingsIcon, Calendar,
} from 'lucide-react';
import RichTextEditor from '../components/admin/RichTextEditor';
import { GlassCard, PageLoading, SectionHeader } from '../components/shared';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { learningApi, madrassaApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { AssignmentRecitationRecorder } from '../components/recitation/AssignmentRecitationRecorder';
import { Link } from 'react-router-dom';
import { Mic } from 'lucide-react';
import { useAssignmentBadges } from '../context/AssignmentBadgesContext';
import { ExerciseSeriesPlayer } from '../components/courses/ExerciseSeriesPlayer';

const ARABIC_LETTERS = [
  'ا','ب','ت','ث','ج','ح','خ','د','ذ','ر','ز','س','ش','ص','ض','ط','ظ',
  'ع','غ','ف','ق','ك','ل','م','ن','ه','و','ي',
];

const SKILL_OPTIONS = [
  { value: 'letters',        label: 'Lettres' },
  { value: 'letter_shapes',  label: 'Formes' },
  { value: 'harakat',        label: 'Ḥarakāt' },
  { value: 'sukun',          label: 'Sukūn' },
  { value: 'shadda',         label: 'Shadda' },
  { value: 'tanwin',         label: 'Tanwīn' },
  { value: 'syllables',      label: 'Syllabes' },
  { value: 'quran_words',    label: 'Mots du Coran' },
  { value: 'ayah_segments',  label: 'Segments' },
];

const DIFFICULTIES = [
  { value: 'easy',   label: 'Facile' },
  { value: 'medium', label: 'Moyen' },
  { value: 'hard',   label: 'Difficile' },
];

const STATUS_META = {
  draft:        { fr: 'Brouillon',  cls: 'bg-surface-elevated text-text-muted border-border' },
  assigned:     { fr: 'Assigné',    cls: 'bg-primary/15 text-primary border-primary/30' },
  in_progress:  { fr: 'En cours',   cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  submitted:    { fr: 'Soumis',     cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  completed:    { fr: 'Terminé',    cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  not_started:  { fr: 'À faire',    cls: 'bg-surface-elevated text-text-muted border-border' },
};

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.draft;
  return <Badge className={`${meta.cls} border text-[10px]`}>{meta.fr}</Badge>;
};

// ---------------------------------------------------------------------------
// Pickers
// ---------------------------------------------------------------------------
const LetterPicker = ({ value, onChange, testid = 'devoir-letters' }) => {
  const set = new Set(value || []);
  const toggle = (l) => {
    const next = new Set(set);
    next.has(l) ? next.delete(l) : next.add(l);
    onChange([...next]);
  };
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider text-text-muted">Lettres ciblées</span>
        <span className="text-[11px] text-text-muted">{set.size} sélectionnée{set.size > 1 ? 's' : ''}</span>
      </div>
      <div className="flex flex-wrap gap-1" data-testid={testid}>
        {ARABIC_LETTERS.map((l) => (
          <button
            key={l}
            type="button"
            onClick={() => toggle(l)}
            className={`font-arabic text-xl w-9 h-9 rounded-md border transition-colors ${
              set.has(l)
                ? 'border-primary/60 bg-primary/15 text-primary'
                : 'border-border bg-surface-elevated/40 text-text-muted hover:border-primary/40'
            }`}
            data-testid={`${testid}-${l}`}
          >
            {l}
          </button>
        ))}
      </div>
    </div>
  );
};

const Toggleables = ({ options, value, onChange, testid }) => {
  const set = new Set(value || []);
  const toggle = (v) => {
    const next = new Set(set);
    next.has(v) ? next.delete(v) : next.add(v);
    onChange([...next]);
  };
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => toggle(o.value)}
          className={`text-xs px-2.5 py-1 rounded-md border transition-colors ${
            set.has(o.value)
              ? 'border-primary/60 bg-primary/15 text-primary'
              : 'border-border bg-surface-elevated/40 text-text-muted hover:border-primary/40'
          }`}
          data-testid={`${testid}-${o.value}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
};

const ClassStudentPicker = ({ classes, selection, onChange }) => {
  // selection: { mode: 'all'|'specific', byClassId: { [cid]: { all: bool, students: Set<uid> } } }
  const setClass = (cid, fn) => {
    const prev = selection.byClassId[cid] || { all: false, students: [] };
    const next = fn(prev);
    onChange({
      ...selection,
      byClassId: { ...selection.byClassId, [cid]: next },
    });
  };
  return (
    <div className="space-y-3" data-testid="devoir-class-picker">
      <span className="text-xs uppercase tracking-wider text-text-muted block">
        Classes & élèves ciblés
      </span>
      {classes.length === 0 && (
        <p className="text-sm text-text-muted">
          Vous n'enseignez dans aucune classe pour le moment.
        </p>
      )}
      {classes.map((cls) => {
        const sel = selection.byClassId[cls.id] || { all: false, students: [] };
        const studSet = new Set(sel.students);
        return (
          <div
            key={cls.id}
            className="rounded-md border border-border bg-surface-elevated/40 p-3 space-y-2"
            data-testid={`devoir-class-${cls.id}`}
          >
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <GraduationCap className="w-4 h-4 text-primary" />
                <span className="text-sm text-text-primary">{cls.name}</span>
                {cls.level && (
                  <Badge variant="outline" className="text-[10px]">{cls.level}</Badge>
                )}
                <Badge variant="outline" className="text-[10px]">
                  {cls.student_count} élève{cls.student_count > 1 ? 's' : ''}
                </Badge>
                {cls.madrassa?.name && (
                  <span className="text-[11px] text-text-muted">
                    · {cls.madrassa.name}
                  </span>
                )}
              </div>
              <label className="text-xs flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={sel.all}
                  onChange={(e) => setClass(cls.id, (p) => ({
                    ...p, all: e.target.checked,
                    students: e.target.checked ? [] : p.students,
                  }))}
                  data-testid={`devoir-class-${cls.id}-all`}
                />
                Toute la classe
              </label>
            </div>
            {!sel.all && cls.students.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {cls.students.map((s) => {
                  const checked = studSet.has(s.user_id);
                  return (
                    <button
                      key={s.user_id}
                      type="button"
                      onClick={() => setClass(cls.id, (p) => {
                        const next = new Set(p.students);
                        next.has(s.user_id) ? next.delete(s.user_id) : next.add(s.user_id);
                        return { ...p, all: false, students: [...next] };
                      })}
                      className={`text-xs px-2 py-1 rounded-md border transition-colors ${
                        checked
                          ? 'border-primary/60 bg-primary/15 text-primary'
                          : 'border-border bg-surface-elevated/30 text-text-muted hover:border-primary/40'
                      }`}
                      data-testid={`devoir-class-${cls.id}-student-${s.user_id}`}
                    >
                      {s.user_name || s.user_email}
                    </button>
                  );
                })}
              </div>
            )}
            {!sel.all && cls.students.length === 0 && (
              <p className="text-[11px] text-text-muted">Cette classe n'a aucun élève.</p>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Create form
// ---------------------------------------------------------------------------
const CreateAssignmentForm = ({ teachingClasses, onCreated }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [instructionsHtml, setInstructionsHtml] = useState('');
  const [letters, setLetters] = useState([]);
  const [skills, setSkills] = useState(['letters']);
  const [difficulty, setDifficulty] = useState(['easy', 'medium']);
  const [count, setCount] = useState(20);
  const [selection, setSelection] = useState({ byClassId: {} });
  const [previewItems, setPreviewItems] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // Lot 42 — recitation type
  const [submissionType, setSubmissionType] = useState('exercise');
  const [recitInstr, setRecitInstr] = useState('');
  const [recitSurah, setRecitSurah] = useState('');
  const [recitFrom, setRecitFrom] = useState('');
  const [recitTo, setRecitTo] = useState('');
  // Lot 43 — finer control
  const [points, setPoints] = useState(20);
  const [maxAttempts, setMaxAttempts] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [publishAt, setPublishAt] = useState('');
  const [visibility, setVisibility] = useState('visible');
  // Section accordion
  const [openAdvanced, setOpenAdvanced] = useState(false);

  const constraints = useMemo(() => ({
    letters: letters.length ? letters : undefined,
    skills:  skills.length ? skills : undefined,
    difficulty: difficulty.length ? difficulty : undefined,
    count: Number(count) || 20,
  }), [letters, skills, difficulty, count]);

  // Compute targets from selection
  const targets = useMemo(() => {
    const classIds = [];
    const studentUserIds = [];
    Object.entries(selection.byClassId || {}).forEach(([cid, sel]) => {
      if (sel.all) classIds.push(cid);
      else (sel.students || []).forEach((uid) => studentUserIds.push(uid));
    });
    return { classIds, studentUserIds };
  }, [selection]);

  const totalTargets = useMemo(() => {
    let n = 0;
    teachingClasses.forEach((cls) => {
      const sel = selection.byClassId[cls.id];
      if (!sel) return;
      if (sel.all) n += cls.student_count || (cls.students?.length || 0);
      else n += (sel.students || []).length;
    });
    return n;
  }, [selection, teachingClasses]);

  const runPreview = async () => {
    setPreviewLoading(true);
    setError(null);
    try {
      const r = await learningApi.previewAssignment(constraints);
      setPreviewItems(r.data);
    } catch (e) {
      setError("Impossible de prévisualiser.");
    } finally {
      setPreviewLoading(false);
    }
  };

  const submit = async () => {
    if (!title.trim()) { setError("Donnez un titre au devoir."); return; }
    if (targets.classIds.length === 0 && targets.studentUserIds.length === 0) {
      setError("Sélectionnez au moins une classe ou un élève.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        title: title.trim(),
        description: description.trim() || null,
        instructionsHtml: instructionsHtml || null,
        classIds: targets.classIds,
        studentUserIds: targets.studentUserIds,
        constraints,
        submissionType,
        points: Number(points) || 20,
        maxAttempts: maxAttempts ? Number(maxAttempts) : null,
        dueDate: dueDate || null,
        publishAt: publishAt || null,
        visibility,
      };
      if (submissionType === 'recitation') {
        payload.recitationConfig = {
          instructions: recitInstr.trim() || null,
          surahNumber: recitSurah ? parseInt(recitSurah, 10) : null,
          ayahFrom: recitFrom ? parseInt(recitFrom, 10) : null,
          ayahTo: recitTo ? parseInt(recitTo, 10) : null,
        };
      }
      await learningApi.createAssignment(payload);
      // reset
      setTitle(''); setDescription(''); setInstructionsHtml('');
      setLetters([]); setPreviewItems(null);
      setSelection({ byClassId: {} });
      setSubmissionType('exercise');
      setRecitInstr(''); setRecitSurah(''); setRecitFrom(''); setRecitTo('');
      setPoints(20); setMaxAttempts(''); setDueDate(''); setPublishAt('');
      setVisibility('visible'); setOpenAdvanced(false);
      onCreated && onCreated();
    } catch (e) {
      setError(e?.response?.data?.detail || "Création impossible.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="devoir-create-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs uppercase tracking-wider text-text-muted">Titre</label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Ex : Lettres Alif à Jim"
            data-testid="devoir-create-title"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-text-muted">Nombre d'exercices</label>
          <Input
            type="number"
            min={1}
            max={50}
            value={count}
            onChange={(e) => setCount(e.target.value)}
            data-testid="devoir-create-count"
          />
        </div>
      </div>

      <div>
        <label className="text-xs uppercase tracking-wider text-text-muted">Description courte (optionnel)</label>
        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Ce que l'élève doit retenir…"
          rows={2}
          data-testid="devoir-create-description"
        />
      </div>

      <div>
        <label className="text-xs uppercase tracking-wider text-text-muted">Instructions détaillées (rich-text, optionnel)</label>
        <RichTextEditor
          testid="devoir-create-instructions-editor"
          value={instructionsHtml}
          onChange={setInstructionsHtml}
          placeholder="Consignes étape par étape, exemples, points de vigilance…"
          minHeight="min-h-[140px]"
        />
      </div>

      {/* Type — Lot 42 */}
      <div>
        <span className="text-xs uppercase tracking-wider text-text-muted block mb-2">Type de devoir</span>
        <div className="flex gap-2" data-testid="devoir-create-type-toggle">
          {[
            { id: 'exercise', label: 'Exercices' },
            { id: 'recitation', label: 'Récitation orale' },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setSubmissionType(t.id)}
              data-testid={`devoir-create-type-${t.id}`}
              className={`px-4 py-2 rounded-lg border text-sm transition-colors ${
                submissionType === t.id
                  ? 'bg-primary/15 border-primary text-primary'
                  : 'bg-surface/40 border-border text-text-secondary hover:border-primary/40'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {submissionType === 'recitation' && (
        <div className="space-y-3 p-3 rounded-lg bg-primary/5 border border-primary/20"
             data-testid="devoir-create-recitation-block">
          <div>
            <label className="text-xs uppercase tracking-wider text-text-muted">Consigne pour l'élève</label>
            <Textarea
              value={recitInstr}
              onChange={(e) => setRecitInstr(e.target.value)}
              placeholder="Ex : Récite avec attention au madd et applique l'idgham…"
              rows={2}
              data-testid="devoir-create-recit-instr"
            />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-xs text-text-muted block mb-1">Sourate</label>
              <Input type="number" min={1} max={114} value={recitSurah}
                     onChange={(e) => setRecitSurah(e.target.value)}
                     placeholder="36"
                     className="bg-surface border-border"
                     data-testid="devoir-create-recit-surah" />
            </div>
            <div>
              <label className="text-xs text-text-muted block mb-1">Verset (de)</label>
              <Input type="number" min={1} value={recitFrom}
                     onChange={(e) => setRecitFrom(e.target.value)}
                     placeholder="1"
                     className="bg-surface border-border"
                     data-testid="devoir-create-recit-from" />
            </div>
            <div>
              <label className="text-xs text-text-muted block mb-1">Verset (à)</label>
              <Input type="number" min={1} value={recitTo}
                     onChange={(e) => setRecitTo(e.target.value)}
                     placeholder="10"
                     className="bg-surface border-border"
                     data-testid="devoir-create-recit-to" />
            </div>
          </div>
          <p className="text-xs text-text-muted">
            L'élève enregistrera sa récitation depuis l'app. Vous l'évaluerez avec une note /100 + commentaire.
          </p>
        </div>
      )}

      {submissionType === 'exercise' && (
        <>
          <LetterPicker value={letters} onChange={setLetters} />

          <div>
            <span className="text-xs uppercase tracking-wider text-text-muted block mb-2">Compétences</span>
            <Toggleables options={SKILL_OPTIONS} value={skills} onChange={setSkills}
                         testid="devoir-create-skills" />
          </div>

          <div>
            <span className="text-xs uppercase tracking-wider text-text-muted block mb-2">Difficultés</span>
            <Toggleables options={DIFFICULTIES} value={difficulty} onChange={setDifficulty}
                         testid="devoir-create-difficulty" />
          </div>
        </>
      )}

      <ClassStudentPicker
        classes={teachingClasses}
        selection={selection}
        onChange={setSelection}
      />

      {totalTargets > 0 && (
        <p className="text-xs text-text-muted" data-testid="devoir-create-targets-count">
          <Users className="w-3 h-3 inline mr-1" />
          {totalTargets} élève{totalTargets > 1 ? 's' : ''} ciblé{totalTargets > 1 ? 's' : ''}
        </p>
      )}

      {/* Paramètres avancés (collapsible) */}
      <div className="border border-border rounded-lg overflow-hidden" data-testid="devoir-create-advanced">
        <button
          type="button"
          onClick={() => setOpenAdvanced((v) => !v)}
          className="w-full flex items-center justify-between gap-2 px-3 py-2.5 bg-surface-elevated/40
                     hover:bg-surface-elevated text-sm text-text-primary"
          data-testid="devoir-create-advanced-toggle"
        >
          <span className="flex items-center gap-2">
            <SettingsIcon className="w-4 h-4 text-text-muted" />
            Paramètres avancés (notes, échéance, visibilité…)
          </span>
          {openAdvanced ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>

        {openAdvanced && (
          <div className="p-3 space-y-3 border-t border-border">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="text-xs uppercase tracking-wider text-text-muted">Note maximale (points)</label>
                <Input
                  type="number"
                  min={0}
                  max={1000}
                  value={points}
                  onChange={(e) => setPoints(e.target.value)}
                  data-testid="devoir-create-points"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-text-muted">Tentatives max (vide = illimité)</label>
                <Input
                  type="number"
                  min={1}
                  max={99}
                  value={maxAttempts}
                  onChange={(e) => setMaxAttempts(e.target.value)}
                  placeholder="ex. 3"
                  data-testid="devoir-create-max-attempts"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-text-muted">Visibilité</label>
                <select
                  value={visibility}
                  onChange={(e) => setVisibility(e.target.value)}
                  className="w-full h-10 rounded-md border border-border bg-surface px-3 text-sm"
                  data-testid="devoir-create-visibility"
                >
                  <option value="visible">Visible immédiatement</option>
                  <option value="scheduled">Publier à une date</option>
                  <option value="hidden">Caché (brouillon)</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-wider text-text-muted flex items-center gap-1">
                  <Calendar className="w-3 h-3" />Échéance (optionnel)
                </label>
                <Input
                  type="datetime-local"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  data-testid="devoir-create-due-date"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-text-muted flex items-center gap-1">
                  <Calendar className="w-3 h-3" />Date de publication (si planifié)
                </label>
                <Input
                  type="datetime-local"
                  value={publishAt}
                  onChange={(e) => setPublishAt(e.target.value)}
                  disabled={visibility !== 'scheduled'}
                  data-testid="devoir-create-publish-at"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {error && (
        <p className="text-sm text-rose-300" data-testid="devoir-create-error">{error}</p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={runPreview}
          disabled={previewLoading}
          data-testid="devoir-create-preview"
        >
          <Eye className="w-3.5 h-3.5 mr-1" />
          {previewLoading ? 'Calcul…' : 'Prévisualiser'}
        </Button>
        <Button
          size="sm"
          onClick={submit}
          disabled={submitting}
          data-testid="devoir-create-submit"
        >
          <Send className="w-3.5 h-3.5 mr-1" />
          {submitting ? 'Envoi…' : 'Créer le devoir'}
        </Button>
      </div>

      {previewItems && (
        <div
          className="rounded-md border border-primary/30 bg-primary/5 p-3 space-y-2"
          data-testid="devoir-create-preview-result"
        >
          <p className="text-sm text-text-primary">
            {previewItems.count} exercices sélectionnés
          </p>
          <div className="flex flex-wrap gap-1">
            {(previewItems.items || []).slice(0, 12).map((it) => (
              <span
                key={it.id}
                className="font-arabic text-base px-2 py-0.5 rounded bg-background/40 border border-border text-primary"
                dir="rtl"
              >
                {it.display?.letter || it.display?.arabicText?.slice(0, 8) || '·'}
              </span>
            ))}
            {previewItems.count > 12 && (
              <span className="text-xs text-text-muted self-center">
                +{previewItems.count - 12}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Assignment row + detail
// ---------------------------------------------------------------------------
const AssignmentRow = ({ a, mode, onOpen, badge }) => {
  const myStatus = mode === 'received' ? (a.mySubmission?.status || 'not_started') : a.status;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full text-left rounded-lg border border-border bg-surface-elevated/40 p-4 hover:border-primary/40 transition-colors flex items-center gap-3 relative"
      data-testid={`devoir-row-${a.id}`}
    >
      <div className={`w-9 h-9 rounded-md flex items-center justify-center shrink-0 ${
        mode === 'received' ? 'bg-amber-500/15 text-amber-300' : 'bg-primary/15 text-primary'
      }`}>
        {mode === 'received' ? <Inbox className="w-4 h-4" /> : <Send className="w-4 h-4" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-text-primary truncate">{a.title}</span>
          <StatusBadge status={myStatus} />
          {a.exerciseIds?.length > 0 && (
            <Badge variant="outline" className="text-[10px]">
              {a.exerciseIds.length} ex.
            </Badge>
          )}
          {mode === 'received' && badge?.receivedUnseen && (
            <Badge
              className="bg-rose-500/15 text-rose-300 border-rose-500/30 border text-[10px]"
              data-testid={`devoir-row-${a.id}-new-badge`}
            >
              Nouveau
            </Badge>
          )}
          {mode === 'sent' && badge?.unseenSubmissions > 0 && (
            <Badge
              className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 border text-[10px]"
              data-testid={`devoir-row-${a.id}-submissions-badge`}
            >
              {badge.unseenSubmissions} nouveau{badge.unseenSubmissions > 1 ? 'x' : ''} résultat{badge.unseenSubmissions > 1 ? 's' : ''}
            </Badge>
          )}
        </div>
        {a.description && (
          <p className="text-xs text-text-muted mt-1 truncate">{a.description}</p>
        )}
        {mode === 'sent' && a.assignedToUserIds?.length > 0 && (
          <p className="text-[11px] text-text-muted mt-1">
            <Users className="w-3 h-3 inline mr-1" />
            {a.assignedToUserIds.length} destinataire{a.assignedToUserIds.length > 1 ? 's' : ''}
            {a.classIds?.length > 0 && (
              <> · {a.classIds.length} classe{a.classIds.length > 1 ? 's' : ''}</>
            )}
          </p>
        )}
      </div>
      <Clock className="w-4 h-4 text-text-muted" />
    </button>
  );
};

const AssignmentDetail = ({ assignmentId, onBack, mode, onSeen }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [, setSubmission] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const refresh = () => {
    setLoading(true);
    learningApi.getAssignment(assignmentId)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refresh();
    // Mark-seen on open: student- and teacher-side both notify the badge ctx.
    const markSeen = async () => {
      try {
        if (mode === 'received') await learningApi.markSeen(assignmentId);
        else if (mode === 'sent') await learningApi.markSubmissionsSeen(assignmentId);
        onSeen && onSeen();
      } catch { /* ignore — non-critical */ }
    };
    markSeen();
    /* eslint-disable-next-line */
  }, [assignmentId, mode]);

  const startSeries = async () => {
    try {
      const sub = await learningApi.startAssignment(assignmentId);
      setSubmission(sub.data);
      setRunning(true);
    } catch { /* surface a toast in a proper UI */ }
  };

  const onSeriesComplete = async (s) => {
    try {
      await learningApi.submitAssignment(assignmentId, {
        score: s.percent,
        correct: s.correct,
        wrong: s.wrong,
        completedExerciseIds: s.answeredIds || [],
      });
      setSubmitted(true);
      onSeen && onSeen();
    } catch { /* ignore */ }
  };

  const exitRunner = () => {
    setRunning(false);
    refresh();
  };

  if (loading) return <PageLoading />;
  if (!data) {
    return (
      <div>
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Retour
        </Button>
        <p className="text-sm text-text-muted mt-3">Devoir introuvable.</p>
      </div>
    );
  }

  if (running) {
    return (
      <div data-testid="devoir-detail-runner">
        <Button
          variant="ghost"
          size="sm"
          onClick={exitRunner}
          data-testid="devoir-runner-back"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Retour au devoir
        </Button>
        <div className="mt-3">
          <ExerciseSeriesPlayer
            exercises={data.exercises}
            testidPrefix="devoir-runner-player"
            onExit={exitRunner}
            onComplete={onSeriesComplete}
          />
        </div>
        {submitted && (
          <p className="text-sm text-emerald-300 mt-3" data-testid="devoir-runner-submitted">
            <CheckCircle2 className="w-4 h-4 inline mr-1" /> Devoir soumis !
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="devoir-detail">
      <Button variant="ghost" size="sm" onClick={onBack} data-testid="devoir-detail-back">
        <ArrowLeft className="w-4 h-4 mr-1" /> Retour
      </Button>

      <GlassCard>
        <div className="flex items-start justify-between flex-wrap gap-2">
          <div>
            <h3 className="text-lg font-serif text-text-primary">{data.title}</h3>
            {data.description && (
              <p className="text-sm text-text-muted mt-1">{data.description}</p>
            )}
          </div>
          <StatusBadge status={mode === 'received' ? (data.mySubmission?.status || 'not_started') : data.status} />
        </div>

        <div className="mt-3 flex flex-wrap gap-2 text-xs text-text-muted">
          <Badge variant="outline" className="text-[10px]">
            <BookOpen className="w-3 h-3 mr-1" /> {data.exerciseIds?.length || 0} exercices
          </Badge>
          {data.classes?.map((c) => (
            <Badge key={c.id} variant="outline" className="text-[10px]">
              <GraduationCap className="w-3 h-3 mr-1" />{c.name}
            </Badge>
          ))}
          {data.constraints?.letters?.length > 0 && (
            <Badge variant="outline" className="text-[10px] font-arabic">
              {data.constraints.letters.join(' ')}
            </Badge>
          )}
          {data.constraints?.skills?.map((s) => (
            <Badge key={s} variant="outline" className="text-[10px]">{s}</Badge>
          ))}
          {data.constraints?.difficulty?.map((d) => (
            <Badge key={d} variant="outline" className="text-[10px]">{d}</Badge>
          ))}
        </div>

        {mode === 'received' && (
          <div className="mt-4">
            {data.submissionType === 'recitation' ? (
              <RecitationStudentBlock assignmentId={assignmentId} cfg={data.recitationConfig} />
            ) : (
              <>
                <Button size="sm" onClick={startSeries} data-testid="devoir-detail-start">
                  <Sparkles className="w-3.5 h-3.5 mr-1" />
                  {data.mySubmission?.status === 'submitted' ? 'Refaire' : 'Commencer'}
                </Button>
                {data.mySubmission?.status === 'submitted' && (
                  <p className="text-xs text-emerald-300 mt-2">
                    Score précédent : {data.mySubmission.score}% (
                    {data.mySubmission.correct}/{(data.mySubmission.correct || 0) + (data.mySubmission.wrong || 0)})
                  </p>
                )}
              </>
            )}
          </div>
        )}
      </GlassCard>

      {/* Creator view: per-student progress */}
      {mode === 'sent' && data.submissions && (
        <GlassCard data-testid="devoir-detail-submissions">
          <h4 className="text-sm font-serif text-text-primary mb-3">Avancement des élèves</h4>
          {data.submissionType === 'recitation' && (
            <div className="mb-3">
              <Button asChild size="sm" variant="outline"
                      data-testid="devoir-detail-grade-recitations-btn">
                <Link to={`/teacher/recitations/${assignmentId}`}>
                  <Mic className="w-3.5 h-3.5 mr-1" /> Évaluer les enregistrements
                </Link>
              </Button>
            </div>
          )}
          {(!data.assignees || data.assignees.length === 0) ? (
            <p className="text-xs text-text-muted">Aucun destinataire défini.</p>
          ) : (
            <div className="space-y-2">
              {data.assignees.map((u) => {
                const sub = data.submissions.find((s) => s.userId === u.user_id);
                const status = sub?.status || 'not_started';
                const isNew = sub?.status === 'submitted'
                  && (sub?.teacherSeenAt == null);
                return (
                  <div key={u.user_id} className="flex items-center justify-between border-b border-border/40 pb-2 text-xs">
                    <span className="text-text-secondary truncate">
                      {u.name || u.email || u.user_id}
                    </span>
                    <div className="flex items-center gap-2">
                      <StatusBadge status={status} />
                      {sub?.score != null && (
                        <span className="text-text-muted">{sub.score}%</span>
                      )}
                      {isNew && (
                        <Badge
                          className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 border text-[10px]"
                          data-testid={`devoir-detail-sub-${u.user_id}-new`}
                        >
                          Nouveau
                        </Badge>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </GlassCard>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export const Devoirs = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth();
  const badgesCtx = useAssignmentBadges();
  const [items, setItems] = useState([]);
  const [teachingClasses, setTeachingClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(() => {
    const t = searchParams.get('tab');
    return ['received', 'sent', 'create'].includes(t) ? t : 'received';
  });
  const [openId, setOpenId] = useState(null);

  // Keep the URL synced when the user switches tab manually, and
  // react to external navigation (e.g. /teacher/assignments/new → ?tab=create).
  useEffect(() => {
    const t = searchParams.get('tab');
    if (t && ['received', 'sent', 'create'].includes(t) && t !== active) {
      setActive(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const handleTabChange = (v) => {
    setActive(v);
    const next = new URLSearchParams(searchParams);
    if (v === 'received') next.delete('tab'); else next.set('tab', v);
    setSearchParams(next, { replace: true });
  };

  const isGlobalAdmin = user?.role === 'admin';
  const isTeacher = teachingClasses.length > 0 || isGlobalAdmin;

  // Indexed badge map for quick lookup in AssignmentRow
  const badgesByAssignment = useMemo(() => {
    const m = {};
    (badgesCtx.byAssignment || []).forEach((b) => { m[b.assignmentId] = b; });
    return m;
  }, [badgesCtx.byAssignment]);

  const refresh = async () => {
    if (!isAuthenticated) { setLoading(false); return; }
    setLoading(true);
    try {
      const [a, c] = await Promise.all([
        learningApi.listAssignments().catch(() => ({ data: { items: [] } })),
        madrassaApi.myTeachingClasses().catch(() => ({ data: { items: [] } })),
      ]);
      setItems(a.data?.items || []);
      setTeachingClasses(c.data?.items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [isAuthenticated]);

  if (!isAuthenticated) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="devoirs-anonymous">
        <SectionHeader
          overline="Devoirs"
          title="Connectez-vous pour accéder à vos devoirs"
          subtitle="Les devoirs sont personnels et liés à votre compte."
        />
        <Button onClick={() => navigate('/login')} className="mt-4">Se connecter</Button>
      </div>
    );
  }

  const myId = user?.id;
  const received = items.filter((a) => (a.assignedToUserIds || []).includes(myId));
  const sent = items.filter((a) => a.assignedByUserId === myId);

  const detailMode = active === 'received' ? 'received' : 'sent';

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="devoirs-page">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/cours')}
        className="text-text-muted hover:text-text-primary mb-4"
        data-testid="devoirs-back-to-cours"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Retour aux cours
      </Button>

      <SectionHeader
        overline="Pratique guidée"
        title="Devoirs"
        subtitle={isTeacher
          ? "Reçois les exercices que ton professeur t'a assignés ou crée des séries pour tes classes."
          : "Reçois les exercices que ton professeur t'a assignés."}
      />

      {openId ? (
        <div className="mt-6">
          <AssignmentDetail
            assignmentId={openId}
            mode={detailMode}
            onSeen={() => badgesCtx.refresh()}
            onBack={() => { setOpenId(null); refresh(); badgesCtx.refresh(); }}
          />
        </div>
      ) : (
        <Tabs value={active} onValueChange={handleTabChange} className="mt-6">
          <TabsList className="bg-surface border border-border">
            <TabsTrigger value="received" data-testid="devoirs-tab-received">
              <Inbox className="w-3.5 h-3.5 mr-1" />
              Reçus
              {received.length > 0 && (
                <Badge variant="outline" className="ml-2 text-[10px]">{received.length}</Badge>
              )}
              {badgesCtx.receivedUnseenCount > 0 && (
                <span
                  className="ml-1.5 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-rose-500/90 text-white text-[10px]"
                  data-testid="devoirs-tab-received-badge"
                >
                  {badgesCtx.receivedUnseenCount}
                </span>
              )}
            </TabsTrigger>
            {isTeacher && (
              <TabsTrigger value="sent" data-testid="devoirs-tab-sent">
                <Send className="w-3.5 h-3.5 mr-1" />
                Envoyés
                {sent.length > 0 && (
                  <Badge variant="outline" className="ml-2 text-[10px]">{sent.length}</Badge>
                )}
                {badgesCtx.submittedUnseenCount > 0 && (
                  <span
                    className="ml-1.5 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-emerald-500/90 text-white text-[10px]"
                    data-testid="devoirs-tab-sent-badge"
                  >
                    {badgesCtx.submittedUnseenCount}
                  </span>
                )}
              </TabsTrigger>
            )}
            {isTeacher && (
              <TabsTrigger value="create" data-testid="devoirs-tab-create">
                <Plus className="w-3.5 h-3.5 mr-1" />
                Créer
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="received" className="space-y-2 mt-4">
            {loading && <p className="text-sm text-text-muted">Chargement…</p>}
            {!loading && received.length === 0 && (
              <p className="text-sm text-text-muted" data-testid="devoirs-received-empty">
                Aucun devoir reçu pour le moment.
              </p>
            )}
            {received.map((a) => (
              <AssignmentRow
                key={a.id}
                a={a}
                mode="received"
                badge={badgesByAssignment[a.id]}
                onOpen={() => setOpenId(a.id)}
              />
            ))}
          </TabsContent>

          {isTeacher && (
            <TabsContent value="sent" className="space-y-2 mt-4">
              {loading && <p className="text-sm text-text-muted">Chargement…</p>}
              {!loading && sent.length === 0 && (
                <p className="text-sm text-text-muted" data-testid="devoirs-sent-empty">
                  Vous n'avez pas encore envoyé de devoir.
                </p>
              )}
              {sent.map((a) => (
                <AssignmentRow
                  key={a.id}
                  a={a}
                  mode="sent"
                  badge={badgesByAssignment[a.id]}
                  onOpen={() => setOpenId(a.id)}
                />
              ))}
            </TabsContent>
          )}

          {isTeacher && (
            <TabsContent value="create" className="mt-4">
              {teachingClasses.length === 0 && !isGlobalAdmin && (
                <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-sm text-amber-300 mb-3"
                     data-testid="devoirs-create-no-classes">
                  <AlertCircle className="w-4 h-4 inline mr-1" />
                  Vous n'enseignez dans aucune classe pour le moment.
                </div>
              )}
              <CreateAssignmentForm
                teachingClasses={teachingClasses}
                onCreated={() => { refresh(); handleTabChange('sent'); }}
              />
            </TabsContent>
          )}
        </Tabs>
      )}

      {!openId && (
        <div className="mt-6 flex flex-wrap items-center justify-between gap-2">
          {!isTeacher && !isGlobalAdmin && (
            <p className="text-[11px] text-text-muted" data-testid="devoirs-not-teacher-hint">
              Pour créer des devoirs, votre rôle d'enseignant doit être validé par un
              administrateur de madrassa.{' '}
              <button
                type="button"
                className="text-primary underline"
                onClick={() => navigate('/madrassa')}
                data-testid="devoirs-go-madrassa-link"
              >
                Aller à mes madrassas
              </button>
            </p>
          )}
          <Button variant="ghost" size="sm" onClick={refresh} data-testid="devoirs-refresh">
            <RefreshCcw className="w-3 h-3 mr-1" /> Rafraîchir
          </Button>
        </div>
      )}
    </div>
  );
};

export default Devoirs;

// ────────────────────────────────────────────────────────────────────────
// RecitationStudentBlock — wrapper around the recorder. Displays the
// recitation context (sourate, verses, instructions) and the latest
// submission status.
// ────────────────────────────────────────────────────────────────────────
const RecitationStudentBlock = ({ assignmentId, cfg }) => {
  const [history, setHistory] = useState([]);
  const refresh = async () => {
    try {
      const r = await import('../services/api').then(m => m.recitationApi.listMine(assignmentId));
      setHistory(r.data || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [assignmentId]);

  return (
    <div className="space-y-4" data-testid="recitation-student-block">
      {cfg?.instructions && (
        <div className="p-3 rounded-lg bg-primary/10 border border-primary/30 text-sm text-text-primary">
          <strong>Consigne :</strong> {cfg.instructions}
        </div>
      )}
      {(cfg?.surahNumber || cfg?.ayahFrom) && (
        <p className="text-xs text-text-muted">
          Récite : sourate {cfg.surahNumber}{cfg?.ayahFrom ? `, versets ${cfg.ayahFrom}${cfg?.ayahTo ? `–${cfg.ayahTo}` : ''}` : ''}
        </p>
      )}
      {history.length > 0 && (
        <div className="space-y-1.5">
          <h4 className="text-[10px] uppercase tracking-wider text-text-muted">Mes envois</h4>
          {history.slice(0, 3).map((rec) => (
            <div key={rec.id} className="text-xs flex justify-between items-center
                                          bg-surface/40 border border-border rounded-lg px-3 py-2"
                 data-testid={`my-recording-${rec.id}`}>
              <span className="text-text-secondary">
                {new Date(rec.created_at).toLocaleString('fr-FR')}
                {rec.duration_sec ? ` · ${Math.round(rec.duration_sec)}s` : ''}
              </span>
              {rec.status === 'graded' ? (
                <span className="text-emerald-300 font-medium">Note : {rec.teacher_score}/100</span>
              ) : (
                <span className="text-amber-300">En attente d'évaluation</span>
              )}
            </div>
          ))}
        </div>
      )}
      <AssignmentRecitationRecorder
        assignmentId={assignmentId}
        onSubmitted={refresh}
      />
    </div>
  );
};
